'use client';

import { useEffect, useRef, useState } from 'react';
import { usePostHog } from 'posthog-js/react';
import type { SubscriptionTier } from '@/lib/subscription';

const PMF_SURVEY_KEY = 'slidetoanki_pmf_survey_done';

const OPTIONS = [
  { value: 'very_disappointed', label: 'Very disappointed' },
  { value: 'somewhat_disappointed', label: 'Somewhat disappointed' },
  { value: 'not_disappointed', label: 'Not disappointed' },
] as const;

interface PmfSurveyProps {
  jobId: string;
  tier: SubscriptionTier;
}

/**
 * One-question Sean Ellis PMF test, shown once per device after an export.
 * 40%+ "very disappointed" is the classic product-market-fit bar.
 */
export function PmfSurvey({ jobId, tier }: PmfSurveyProps) {
  const posthog = usePostHog();
  const [visible, setVisible] = useState(false);
  const [answered, setAnswered] = useState(false);
  const shownTracked = useRef(false);

  useEffect(() => {
    try {
      if (localStorage.getItem(PMF_SURVEY_KEY)) return;
    } catch {
      return;
    }
    // Let the export success state land first before asking.
    const timer = setTimeout(() => setVisible(true), 5000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (visible && !shownTracked.current) {
      shownTracked.current = true;
      posthog?.capture('pmf_survey_shown', { job_id: jobId, current_tier: tier });
    }
  }, [visible, jobId, tier, posthog]);

  if (!visible) return null;

  const answer = (value: string) => {
    posthog?.capture('pmf_survey_answered', {
      job_id: jobId,
      current_tier: tier,
      answer: value,
    });
    try {
      localStorage.setItem(PMF_SURVEY_KEY, value);
    } catch {
      // Storage unavailable — the survey just shows again next export.
    }
    setAnswered(true);
  };

  return (
    <div className="mt-4 rounded-2xl border border-border bg-card px-5 py-4" aria-label="Product feedback survey">
      {answered ? (
        <p className="text-sm text-muted">Thanks — that really helps shape what we build next.</p>
      ) : (
        <>
          <p className="text-sm font-medium text-foreground">
            Quick question — how would you feel if you could no longer use SlideToAnki?
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            {OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => answer(option.value)}
                className="rounded-xl border border-border px-4 py-2 text-sm font-medium text-foreground transition hover:border-primary hover:text-primary"
              >
                {option.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
